import dayjs from 'dayjs';
import {
  CLIENT_OPTIONAL_TEXT_FIELD_MAX_LENGTH,
  trimOptionalClientText,
  normalizeClientPhoneForDedup,
} from '../../../utils/clientMapper';
import { normalizeClientBirthDate } from '../../../utils/clientFormValues';

export const CLIENT_IMPORT_EXPORT_HEADERS = [
  'Nom',
  'Téléphone',
  'Email',
  'Ville',
  'Quartier',
  'Adresse (complément)',
  'Type de client',
  'Date de naissance (JJ/MM/AAAA)',
  'Opt-in WhatsApp (oui/non)',
] as const;

export type ClientImportDraftRow = {
  rowIndex: number;
  name: string;
  phone: string;
  email: string;
  city: string;
  district: string;
  address: string;
  clientType: string;
  birthDateRaw: string;
  birthDate?: Date | null;
  marketingOptInRaw: string;
  marketingOptIn?: boolean;
};

export type ClientImportFieldKey =
  | 'name'
  | 'phone'
  | 'email'
  | 'city'
  | 'district'
  | 'address'
  | 'clientType'
  | 'birthDate'
  | 'marketingOptIn';

export const CLIENT_IMPORT_FIELD_LABELS: Record<ClientImportFieldKey, string> = {
  name: 'Nom',
  phone: 'Téléphone',
  email: 'Email',
  city: 'Ville',
  district: 'Quartier',
  address: 'Adresse (complément)',
  clientType: 'Type de client',
  birthDate: 'Date de naissance',
  marketingOptIn: 'Opt-in WhatsApp',
};

export const CLIENT_IMPORT_REQUIRED_FIELDS: ClientImportFieldKey[] = ['name'];

export const CLIENT_IMPORT_FIELD_KEYS: ClientImportFieldKey[] = [
  'name',
  'phone',
  'email',
  'city',
  'district',
  'address',
  'clientType',
  'birthDate',
  'marketingOptIn',
];

export type ClientImportColumnMapping = {
  name: number;
  phone?: number;
  email?: number;
  city?: number;
  district?: number;
  address?: number;
  clientType?: number;
  birthDate?: number;
  marketingOptIn?: number;
};

function norm(s: string): string {
  return s.trim().toLowerCase();
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function parseMarketingOptInCell(
  raw: unknown
): { value?: boolean; error?: string } {
  if (raw === undefined || raw === null || String(raw).trim() === '') {
    return { value: undefined };
  }
  const s = String(raw).trim().toLowerCase();
  if (['oui', 'yes', '1', 'true', 'vrai', 'o'].includes(s)) return { value: true };
  if (['non', 'no', '0', 'false', 'faux', 'n'].includes(s)) return { value: false };
  return { error: 'Opt-in WhatsApp : utilisez oui ou non.' };
}

export function parseClientBirthDateCell(
  raw: unknown
): { value?: Date | null; error?: string } {
  if (raw === undefined || raw === null || String(raw).trim() === '') {
    return { value: null };
  }
  if (raw instanceof Date) {
    return { value: normalizeClientBirthDate(raw) };
  }
  if (dayjs.isDayjs(raw)) {
    return { value: normalizeClientBirthDate(raw) };
  }
  const s = String(raw).trim();
  const frMatch = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (frMatch) {
    const day = Number(frMatch[1]);
    const month = Number(frMatch[2]);
    const year = Number(frMatch[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return { error: 'Date de naissance invalide (JJ/MM/AAAA).' };
    }
    const d = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));
    if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) {
      return { error: 'Date de naissance invalide (JJ/MM/AAAA).' };
    }
    return { value: d };
  }
  const parsed = dayjs(s);
  if (parsed.isValid()) {
    return { value: normalizeClientBirthDate(parsed) };
  }
  return { error: 'Date de naissance invalide (JJ/MM/AAAA).' };
}

export function mapHeaderToClientField(headerCell: unknown): ClientImportFieldKey | null {
  const h = String(headerCell ?? '').trim();
  const n = norm(h);
  if (!n) return null;
  if (n === 'nom' || n === 'name' || n === 'client') return 'name';
  if (n.includes('telephone') || n.includes('téléphone') || n === 'tel' || n === 'phone') {
    return 'phone';
  }
  if (n === 'email' || n === 'mail' || n === 'e-mail') return 'email';
  if (n === 'ville' || n === 'city') return 'city';
  if (n === 'quartier' || n === 'district') return 'district';
  if (n.includes('adresse') || n === 'address') return 'address';
  if (n.includes('type') && n.includes('client')) return 'clientType';
  if (n.includes('naissance') || n.includes('birth')) return 'birthDate';
  if (n.includes('opt-in') || n.includes('optin') || n.includes('whatsapp')) return 'marketingOptIn';
  return null;
}

export function suggestClientMappingFromHeaderRow(
  headerRow: unknown[]
): Partial<Record<ClientImportFieldKey, number>> {
  const m: Partial<Record<ClientImportFieldKey, number>> = {};
  for (let c = 0; c < headerRow.length; c++) {
    const f = mapHeaderToClientField(headerRow[c]);
    if (f && m[f] === undefined) {
      m[f] = c;
    }
  }
  return m;
}

export function validateClientColumnMappingDuplicates(
  mapping: Partial<ClientImportColumnMapping>
): string | null {
  const byCol = new Map<number, ClientImportFieldKey[]>();
  (Object.entries(mapping) as [ClientImportFieldKey, number | undefined][]).forEach(([key, col]) => {
    if (col === undefined) return;
    if (!byCol.has(col)) byCol.set(col, []);
    byCol.get(col)!.push(key);
  });
  for (const [col, keys] of Array.from(byCol.entries())) {
    if (keys.length > 1) {
      const labels = keys.map((k) => CLIENT_IMPORT_FIELD_LABELS[k]).join(', ');
      return `La colonne ${col + 1} est utilisée plusieurs fois : ${labels}.`;
    }
  }
  return null;
}

function cellAt(line: unknown[], col: number | undefined): unknown {
  if (col === undefined || col < 0) return undefined;
  return line[col];
}

function stringCell(raw: unknown): string {
  return String(raw ?? '').trim();
}

export function parseClientImportMatrixWithMapping(
  rows: unknown[][],
  headerRowIndex: number,
  mapping: ClientImportColumnMapping
): ClientImportDraftRow[] {
  if (!rows.length) return [];
  const dataRows = rows.slice(headerRowIndex + 1);
  const drafts: ClientImportDraftRow[] = [];
  let rowIndex = headerRowIndex + 2;

  for (const line of dataRows) {
    const birthRaw = cellAt(line, mapping.birthDate);
    const optInRaw = cellAt(line, mapping.marketingOptIn);
    const birthParsed = parseClientBirthDateCell(birthRaw);
    const optInParsed = parseMarketingOptInCell(optInRaw);

    const name = stringCell(cellAt(line, mapping.name));
    const phone = stringCell(cellAt(line, mapping.phone));
    const email = stringCell(cellAt(line, mapping.email));
    const city = stringCell(cellAt(line, mapping.city));
    const district = stringCell(cellAt(line, mapping.district));
    const address = stringCell(cellAt(line, mapping.address));
    const clientType = stringCell(cellAt(line, mapping.clientType));

    const isEmptyLine =
      !name &&
      !phone &&
      !email &&
      !city &&
      !district &&
      !address &&
      !clientType &&
      String(birthRaw ?? '').trim() === '' &&
      String(optInRaw ?? '').trim() === '';

    if (isEmptyLine) {
      rowIndex += 1;
      continue;
    }

    drafts.push({
      rowIndex,
      name,
      phone,
      email,
      city,
      district,
      address,
      clientType,
      birthDateRaw: stringCell(birthRaw),
      birthDate: birthParsed.error ? undefined : birthParsed.value,
      marketingOptInRaw: stringCell(optInRaw),
      marketingOptIn: optInParsed.error ? undefined : optInParsed.value,
    });
    rowIndex += 1;
  }

  return drafts;
}

export function parseClientImportMatrix(rows: unknown[][]): ClientImportDraftRow[] {
  if (!rows.length) return [];

  let headerRowIndex = 0;
  const first = rows[0] || [];
  const fieldByCol: Record<number, ClientImportFieldKey> = {};
  for (let c = 0; c < first.length; c++) {
    const field = mapHeaderToClientField(first[c]);
    if (field) fieldByCol[c] = field;
  }

  if (!Object.values(fieldByCol).includes('name')) {
    headerRowIndex = -1;
    for (let r = 0; r < Math.min(rows.length, 15); r++) {
      const trial: Record<number, ClientImportFieldKey> = {};
      const line = rows[r] || [];
      for (let c = 0; c < line.length; c++) {
        const field = mapHeaderToClientField(line[c]);
        if (field) trial[c] = field;
      }
      if (Object.values(trial).includes('name')) {
        headerRowIndex = r;
        Object.assign(fieldByCol, trial);
        break;
      }
    }
  }

  if (headerRowIndex < 0 || !Object.values(fieldByCol).includes('name')) {
    return [];
  }

  const mapping: Partial<ClientImportColumnMapping> = {};
  for (const [colStr, field] of Object.entries(fieldByCol)) {
    const col = Number(colStr);
    (mapping as Record<ClientImportFieldKey, number>)[field] = col;
  }
  if (mapping.name === undefined) return [];

  return parseClientImportMatrixWithMapping(
    rows,
    headerRowIndex,
    mapping as ClientImportColumnMapping
  );
}

export type ClientRowValidationIssue = { rowIndex: number; messages: string[] };

export function validateClientImportDrafts(
  drafts: ClientImportDraftRow[],
  allowedClientTypes: string[],
  existingPhonesNormalized: Set<string>
): ClientRowValidationIssue[] {
  const issues: ClientRowValidationIssue[] = [];
  const phoneInFile = new Map<string, number>();
  const allowedTypesLower = new Map(
    allowedClientTypes.map((t) => [norm(t), t] as const)
  );

  for (const d of drafts) {
    const messages: string[] = [];
    const name = d.name.trim();

    if (!name) {
      messages.push('Le nom est obligatoire.');
    } else if (name.length < 2) {
      messages.push('Le nom doit contenir au moins 2 caractères.');
    } else if (name.length > 100) {
      messages.push('Le nom ne peut pas dépasser 100 caractères.');
    }

    const phone = d.phone.trim();
    if (phone && !/^[0-9+\s\-()]+$/.test(phone)) {
      messages.push('Numéro de téléphone invalide.');
    }

    const email = d.email.trim();
    if (email && !EMAIL_PATTERN.test(email)) {
      messages.push("Format d'email invalide.");
    }

    for (const [label, value] of [
      ['Ville', d.city],
      ['Quartier', d.district],
      ['Adresse', d.address],
    ] as const) {
      const trimmed = value.trim();
      if (trimmed.length > CLIENT_OPTIONAL_TEXT_FIELD_MAX_LENGTH) {
        messages.push(`${label} : maximum ${CLIENT_OPTIONAL_TEXT_FIELD_MAX_LENGTH} caractères.`);
      }
    }

    const clientType = d.clientType.trim();
    if (clientType) {
      if (allowedClientTypes.length === 0) {
        messages.push('Aucun type de client configuré pour ce business.');
      } else if (!allowedTypesLower.has(norm(clientType))) {
        messages.push(`Type de client inconnu : « ${clientType} ».`);
      }
    }

    if (d.birthDateRaw && d.birthDate === undefined) {
      const reparsed = parseClientBirthDateCell(d.birthDateRaw);
      if (reparsed.error) messages.push(reparsed.error);
    }

    if (d.marketingOptInRaw) {
      const reparsed = parseMarketingOptInCell(d.marketingOptInRaw);
      if (reparsed.error) messages.push(reparsed.error);
    }

    const phoneNorm = phone ? normalizeClientPhoneForDedup(phone) : '';
    if (phoneNorm) {
      if (existingPhonesNormalized.has(phoneNorm)) {
        messages.push('Ce numéro existe déjà dans la base clients du business.');
      }
      if (phoneInFile.has(phoneNorm)) {
        messages.push(`Téléphone en double avec la ligne ${phoneInFile.get(phoneNorm)}.`);
      } else {
        phoneInFile.set(phoneNorm, d.rowIndex);
      }
    }

    if (messages.length) {
      issues.push({ rowIndex: d.rowIndex, messages });
    }
  }

  return issues.sort((a, b) => a.rowIndex - b.rowIndex);
}

export function clientIssuesByRowIndex(issues: ClientRowValidationIssue[]): Map<number, string[]> {
  const m = new Map<number, string[]>();
  for (const i of issues) {
    m.set(i.rowIndex, i.messages);
  }
  return m;
}

export function clientImportHasBlockingIssues(issues: ClientRowValidationIssue[]): boolean {
  return issues.length > 0;
}

/** Payload prêt pour la persistance Firestore. */
export type ClientImportPersistRow = {
  name: string;
  phone?: string;
  email?: string;
  city?: string;
  district?: string;
  address?: string;
  clientType?: string;
  birthDate?: Date;
  marketingOptIn?: boolean;
};

export function buildClientImportPersistRow(
  draft: ClientImportDraftRow,
  allowedClientTypes: string[]
): ClientImportPersistRow {
  const allowedTypesLower = new Map(
    allowedClientTypes.map((t) => [norm(t), t] as const)
  );
  const clientTypeRaw = trimOptionalClientText(draft.clientType);
  const clientType =
    clientTypeRaw && allowedTypesLower.has(norm(clientTypeRaw))
      ? allowedTypesLower.get(norm(clientTypeRaw))
      : undefined;

  const row: ClientImportPersistRow = {
    name: draft.name.trim(),
  };

  const phone = trimOptionalClientText(draft.phone);
  if (phone) row.phone = phone;

  const email = trimOptionalClientText(draft.email);
  if (email) row.email = email;

  const city = trimOptionalClientText(draft.city);
  if (city) row.city = city;

  const district = trimOptionalClientText(draft.district);
  if (district) row.district = district;

  const address = trimOptionalClientText(draft.address);
  if (address) row.address = address;

  if (clientType) row.clientType = clientType;

  if (draft.birthDate) row.birthDate = draft.birthDate;

  if (draft.marketingOptIn !== undefined) {
    row.marketingOptIn = draft.marketingOptIn;
  }

  return row;
}
